<script setup lang="ts">
import {
  Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions,
  TransitionChild, TransitionRoot,
  Dialog, DialogPanel,
} from '@headlessui/vue'
import * as yup from 'yup'
import { useRoute } from 'vue-router'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { ErrorMessage, Field, Form } from 'vee-validate'
import { merge, omit, sortBy } from 'lodash'
import { computed, onMounted, reactive, ref } from 'vue'
import { H1Icon, KeyIcon } from '@heroicons/vue/16/solid'
import { CheckCircleIcon, CheckIcon, ChevronUpDownIcon, XCircleIcon, PlusCircleIcon } from '@heroicons/vue/20/solid'
import router from '@/router.ts'
import { FindProjectAndAllUsers, FindProjectsQuery } from '@/components/projects/graphql/project.queries.ts'
import { FindUsersCompactQuery } from '@/components/users/graphql/user.queries.ts'
import { CreateOrUpdateProjectMutation } from '@/components/projects/graphql/project.mutations.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { useToast } from 'vue-toastification'
import type { User } from '../users/interfaces.ts'
import type { Project } from '../projects/interfaces.ts'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'
import { storeToRefs } from 'pinia'

function onCancelButtonClicked () {
  router.push({ path: `/projects` })
}

function getDefaultFormState (): Project {
  return {
    id: null,
    key: '',
    chatsAmount: 0,
    chatsActiveAmount: 0,
    title: '',
    description: '',
    userIds: [],
  }
}

const { setProjects: setUserProjects, getProjects: getUserProjects } = useUserStore()
const { hasPresentationModeEnabled } = storeToRefs(useUserStore())
const toast = useToast()
const route = useRoute()
const { t } = useTranslations()
const form = reactive<Project>(getDefaultFormState())
const users = ref<User[]>([])
const usersSearchQuery = ref('')
const usersSelected = ref<User[]>([])
const usersFiltered = computed(() => {
  const queryLowercased = usersSearchQuery.value.toLowerCase()
  return usersSearchQuery.value === ''
    ? users.value
    : users.value.filter(({ username, firstname, lastname }) => {
      const isUsernameMatched = username.toLowerCase().includes(queryLowercased)
      const isFirstnameMatched = firstname.toLowerCase().includes(queryLowercased)
      const isLastnameMatched = lastname.toLowerCase().includes(queryLowercased)

      return isUsernameMatched || isFirstnameMatched || isLastnameMatched
    })
})
const validationSchema = computed( () =>
  yup.object().shape({
  title: yup.string()
    .min(5, `${t('projects.addOrEditModal.title')}: ${t('common.validation.minLength', { min: 5 })}`)
    .max(64, `${t('projects.addOrEditModal.title')}: ${t('common.validation.maxLength', { max: 64 })}`),
  description: yup.string()
    .min(3, `${t('projects.addOrEditModal.projectKey')}: ${t('common.validation.minLength', { min: 3 })}`)
    .max(256, `${t('projects.addOrEditModal.projectKey')}: ${t('common.validation.maxLength', { max: 256 })}`)
    .required(t('common.validation.required')),
  key: yup.string()
    .matches(/^[a-z1-9]+(?:[_-][a-z1-9]+)*$/, t('project key: a-z, 1-9, no spaces, underscores and dashes allowed'))
    .min(3, `${t('common.validation.minLength', { min: 3 })}`)
    .max(32, `${t('common.validation.maxLength', { max: 32 })}`)
    .required(t('common.validation.required')),
}))
const isEditMode = ref<boolean>(route.path.endsWith('/edit'))
const {
  onResult: onProjectAndUsersLoaded,
  onError: onProjectAndUsersLoadingFailed,
  load: loadProjectAndUsers,
} = useLazyQuery<{
  users: User[],
  projects: Project[],
}>(FindProjectAndAllUsers, {
  variables: {
    input: {
      projectIds: [parseInt(route.params.projectId as string)]
    }
  }
})
const {
  onResult: onUsersLoaded,
  onError: onUsersLoadingFailed,
  load: loadUsers
} = useLazyQuery<{
  users: User[],
}>(FindUsersCompactQuery)
const {
  onDone: onSubmitSucceed,
  onError: onSubmitFailed,
  mutate: submit
} = useMutation<{
  project: Project
}>(CreateOrUpdateProjectMutation)

onProjectAndUsersLoaded(({ users: gqlUsers, projects: gqlProjects }) => {
  if (gqlProjects.length !== 1) {
    toast.error('Project not found')
    return
  }

  const project = gqlProjects[0] as Project
  Object.assign(form, project)
  users.value = gqlUsers
  usersSelected.value = gqlUsers.filter(({ id }) => project.userIds.includes(id as number))
})

onProjectAndUsersLoadingFailed(() => {
})

onUsersLoaded(({ users: gqlUsers }) => {
  users.value = gqlUsers
})

onUsersLoadingFailed(() => {
  toast.error(`Failed to load users list`)
})

// @ts-expect-error Jevgeni fix it!
onSubmitSucceed(({ data: { project }}) => {
  if (isEditMode.value) {
    toast.success(`Project "${project.title}" saved`)
  } else {
    toast.success(`Project "${project.title}" added`)
  }
})

onSubmitFailed((err) => {
  if (isEditMode.value) {
    toast.error(`Failed to save project`)
  } else {
    toast.error(`Failed to add project`)
  }
})

function onSubmit (values: any) { // TODO: Interface
  submit({
    variables: {
      input: merge(omit(values), {
        userIds: usersSelected.value.map(({ id }) => id),
      })
    },
    // @ts-expect-error Jevgeni fix it later
    update(proxy, { data: { project: gqlProject } }) {
      // Update projects in projects view
      // @ts-expect-error Jevgeni fix it later
      const { projects } = proxy.readQuery<{ projects: Project[] | null }>({
        query: FindProjectsQuery
      })

      proxy.writeQuery({
        query: FindProjectsQuery,
        data: {
          projects: isEditMode.value
            // @ts-expect-error Jevgeni fix it
            ? projects.map((project) => project.id === gqlProject.id ? gqlProject : project)
            : [gqlProject].concat(projects)
        }
      })

      // Update projects in header dropdown
      const userProjects = getUserProjects().concat({
        id: gqlProject.id,
        key: gqlProject.key,
        title: gqlProject.title,
        __typename: 'Project',
      } as unknown as Project[])

      setUserProjects(sortBy(userProjects, 'title'))
      router.push({ path: `/projects` }) // TODO: Enum with urls
    }
  })
}

onMounted(async () => {
  console.log('onMounted')
  try {
    if (isEditMode.value) {
      await loadProjectAndUsers()
    } else {
      await loadUsers()
      Object.assign(form, getDefaultFormState())
    }
    await (isEditMode.value ? loadProjectAndUsers() : loadUsers())
  } catch {
  }
})
</script>
<template>
  <main>
    <TransitionRoot :show="true">
      <Dialog class="relative z-10">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>
        <div class="fixed inset-0 z-10 w-screen">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <Form :validation-schema="validationSchema" @submit="onSubmit">
                    <Field
                      type="hidden"
                      :modelValue="form.id"
                      id="id"
                      name="id"
                      class="hidden"
                    />
                    <div class="space-y-2">
                      <div class="border-b border-gray-900/10 pb-6">
                        <div class="flex justify-between">
                          <h2 class="text-base/7 font-semibold text-gray-900">
                            {{ isEditMode ? t('projects.addOrEditModal.editProject') : t('projects.addOrEditModal.newProject') }}
                          </h2>
                          <div class="ml-5 text-xs text-red-500">
                            <ErrorMessage name="title" class="block"/>
                            <ErrorMessage name="description" class="block"/>
                            <ErrorMessage name="key" class="block"/>
                          </div>
                        </div>
                        <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-6">
                          <div class="sm:col-span-6">
                            <label class="block text-sm/6 font-light text-gray-900">{{ t('projects.addOrEditModal.title') }}</label>
                            <div class="mt-2 grid grid-cols-1">
                              <Field
                                type="text"
                                :modelValue="form.title"
                                id="title"
                                name="title"
                                :class="{
                                  'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 placeholder:text-gray-400 sm:pl-9 sm:text-sm/6': true,
                                  'outline-solid outline-1 -outline-offset-1 outline-gray-300 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600': true,
                                  'blurry-text': hasPresentationModeEnabled,
                                }"
                              />
                              <H1Icon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                            </div>
                          </div>
                          <div class="sm:col-span-6">
                            <label class="block text-sm/6 font-light text-gray-900">
                              {{ t('projects.addOrEditModal.projectKey') }}
                            </label>
                            <div class="mt-2 grid grid-cols-1">
                              <Field
                                type="text"
                                :modelValue="form.key"
                                :disabled="isEditMode"
                                id="key"
                                name="key"
                                :class="{
                                  'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 placeholder:text-gray-400 sm:pl-9 sm:text-sm/6': true,
                                  'outline-solid outline-1 -outline-offset-1 outline-gray-300 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600': true,
                                  'blurry-text': hasPresentationModeEnabled,
                                  'opacity-60': isEditMode,
                                }"
                              />
                              <KeyIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                            </div>
                          </div>
                          <div class="sm:col-span-6">
                            <label class="block text-sm/6 font-light text-gray-900">
                              {{ t('projects.addOrEditModal.description') }}
                            </label>
                            <div class="mt-2 grid grid-cols-1">
                              <Field
                                rows="4"
                                :modelValue="form.description"
                                as="textarea"
                                id="description"
                                name="description"
                                :class="{
                                  'focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 outline-solid outline-1 -outline-offset-1 outline-gray-300': true,
                                  'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 sm:text-sm/6 min-h-28 max-h-28': true,
                                  'blurry-text': hasPresentationModeEnabled,
                                }"
                              >
                              </Field>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="border-b border-gray-900/10 pb-6">
                        <h2 class="text-base/7 font-light text-gray-900">{{ t('projects.addOrEditModal.usersAccess') }} ({{ usersSelected.length }})</h2>
                        <span
                          v-if="usersSelected.length === 0"
                          class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10"
                        >
                          {{ t('projects.addOrEditModal.pleaseChooseUsers') }}
                        </span>
                        <span
                          v-for="user in usersSelected"
                          :class="{
                            'inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 mr-1': true,
                            'blurry-text': hasPresentationModeEnabled,
                          }"
                        >
                          {{ user.firstname }} {{ user.lastname }}
                        </span>
                        <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                          <div class="sm:col-span-12">
                            <Combobox as="div" v-model="usersSelected" @update:modelValue="usersSearchQuery = ''" multiple>
                              <div class="relative mt-2">
                                <ComboboxInput
                                  class="block w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 outline-solid outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                  @change="usersSearchQuery = $event.target.value"
                                  @blur="usersSearchQuery = ''"
                                />
                                <!-- :display-value="(projects) => projects.values().reduce((acc, { title }) => `${acc},${title}`, '')" -->
                                <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                                  <ChevronUpDownIcon class="size-5 text-gray-400" aria-hidden="true" />
                                </ComboboxButton>
                                <ComboboxOptions v-if="usersFiltered.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
                                  <ComboboxOption v-for="user in usersFiltered" :key="user.id as number" :value="user" as="template" v-slot="{ active, selected }">
                                    <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white outline-hidden' : 'text-gray-900']">
                                      <div class="flex items-center">
                                        <!-- TODO: Replace 5 > 6 on condition -->
                                        <span :class="['inline-block size-2 shrink-0 rounded-full', 5 > 6 ? 'bg-green-400' : 'bg-gray-200']" aria-hidden="true" />
                                        <span :class="['ml-3 truncate', selected && 'font-semibold', hasPresentationModeEnabled && 'blurry-text']">
                                          {{ user.firstname }} {{ user.lastname }} ({{ user.username }})
                                          <!--TODO Restricted or not <span class="sr-only"> is {{ project.active ? 'online' : 'offline' }}</span>-->
                                        </span>
                                      </div>
                                      <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                                        <CheckIcon class="size-5" aria-hidden="true" />
                                        </span>
                                    </li>
                                  </ComboboxOption>
                                </ComboboxOptions>
                              </div>
                            </Combobox>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <CrdButton
                        :variant="ButtonVariant.Save"
                        class="w-full justify-items-center outline-solid"
                        :type="ButtonType.Submit"
                      >
                        <CheckCircleIcon v-if="isEditMode" class="block size-5" aria-hidden="true" />
                        <PlusCircleIcon v-else class="block size-5" aria-hidden="true"></PlusCircleIcon>

                        <span>{{ t(isEditMode ? 'common.buttons.save' : 'common.buttons.add') }}</span>
                      </CrdButton>
                      <CrdButton
                        :variant="ButtonVariant.Cancel"
                        @click="onCancelButtonClicked"
                      >
                        <XCircleIcon class="block size-5" aria-hidden="true" />
                        {{ t('common.buttons.cancel') }}
                      </CrdButton>
                    </div>
                  </Form>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </main>
</template>

